import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Routes, Route, NavLink, Navigate, useNavigate } from "react-router-dom";
import {
  Building2, FileBarChart2, AlertTriangle, ShieldCheck, Database, Table2, BookOpen,
  Network, Grid3x3, GitBranch, X, ChevronRight, Search, Lock,
} from "lucide-react";
import {
  loadContract, reconstructBalanceSheet, periodRoles, atomsForCell, isSentinel, humanize,
} from "./api";

/* ---------------- shared ---------------- */
const ContractCtx = createContext(null);
const useModel = () => useContext(ContractCtx);

const STATUS_COLORS = {
  PASS: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  FAIL: "bg-rose-50 text-rose-700 ring-rose-600/20",
  RUNNING: "bg-sky-50 text-sky-700 ring-sky-600/20",
  BLOCKED: "bg-amber-50 text-amber-800 ring-amber-600/20",
  NOT_RUN: "bg-stone-100 text-stone-600 ring-stone-500/20",
  NOT_PROVEN: "bg-amber-50 text-amber-800 ring-amber-600/20",
  NOT_CAPTURED: "bg-stone-100 text-stone-500 ring-stone-400/20",
  STALE: "bg-amber-50 text-amber-800 ring-amber-600/20",
  INVALID: "bg-rose-50 text-rose-700 ring-rose-600/20",
  FINANCIAL_ELIGIBLE: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  UNCLASSIFIED: "bg-stone-100 text-stone-600 ring-stone-500/20",
};
const StatusBadge = ({ value }) => {
  const key = String(value ?? "").toUpperCase();
  const cls = STATUS_COLORS[key];
  if (!cls) return <span className="text-slate-700">{value ?? "—"}</span>;
  return <span data-testid={`status-${key}`} className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-bold ring-1 ring-inset ${cls}`}>{key}</span>;
};
const KeyBadge = ({ kind }) => (
  <span className={`mr-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-extrabold ${kind === "PK" ? "bg-indigo-50 text-indigo-700" : "bg-orange-50 text-orange-700"}`}>{kind}</span>
);
const Panel = ({ children, className = "", ...rest }) => (
  <div className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${className}`} {...rest}>{children}</div>
);
const SectionHead = ({ eyebrow, title, caption, right }) => (
  <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
    <div>
      {eyebrow && <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{eyebrow}</div>}
      <h2 className="text-2xl font-extrabold text-slate-900">{title}</h2>
      {caption && <p className="mt-1 max-w-3xl text-sm text-slate-500">{caption}</p>}
    </div>
    {right}
  </div>
);
const cellVal = (v) => (isSentinel(v) ? <em className="text-slate-400">{String(v ?? "NULL")}</em> : String(v));

function DataTable({ records, testid, onRow, columns, pageSize = 10 }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const cols = useMemo(() => columns || Array.from(new Set(records.slice(0, 40).flatMap((r) => Object.keys(r)))), [records, columns]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? records.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(s))) : records;
  }, [records, q]);
  useEffect(() => setPage(0), [q, records]);
  const paged = filtered.slice(page * pageSize, page * pageSize + pageSize);
  const pages = Math.ceil(filtered.length / pageSize) || 1;
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-slate-400" />
          <input data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            className="w-72 rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-sm outline-none focus:border-indigo-400" />
        </div>
        <span className="text-xs text-slate-400">{filtered.length} records</span>
      </div>
      <div className="overflow-auto rounded-lg border border-slate-200" style={{ maxHeight: 560 }}>
        <table className="w-full border-collapse text-sm" data-testid={testid}>
          <thead className="sticky top-0 bg-slate-50">
            <tr>{cols.map((c) => <th key={c} className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-left font-bold text-slate-700">{humanize(c)}<div className="font-mono text-[10px] font-medium text-slate-400">{c}</div></th>)}</tr>
          </thead>
          <tbody>
            {paged.map((r, i) => (
              <tr key={r.id || i} data-testid={`${testid}-row`} onClick={() => onRow?.(r)} className={`border-b border-slate-100 ${onRow ? "cursor-pointer hover:bg-indigo-50/40" : ""}`}>
                {cols.map((c) => {
                  const up = String(r[c] ?? "").toUpperCase();
                  return <td key={c} className="max-w-xs truncate px-3 py-1.5 text-slate-700">{STATUS_COLORS[up] ? <StatusBadge value={up} /> : cellVal(r[c])}</td>;
                })}
              </tr>
            ))}
            {paged.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-slate-400">No records match.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex items-center justify-end gap-2 text-sm">
        <button className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</button>
        <span className="text-slate-500">Page {page + 1} / {pages}</span>
        <button className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>Next</button>
      </div>
    </div>
  );
}

const Drawer = ({ open, onClose, title, children }) =>
  !open ? null : (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="drawer">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className="relative h-full w-[520px] overflow-auto bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-extrabold text-slate-900">{title}</h3>
          <button data-testid="drawer-close" onClick={onClose} className="rounded p-1 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );

/* ---------------- Balance Sheet ---------------- */
function BalanceSheetPage() {
  const model = useModel();
  const reports = model.records.report_instances;
  const years = reports.map((r) => r.financial_year);
  const [company] = useState(model.companies[0] || "HDFC Bank");
  const [year, setYear] = useState(years[0]);
  const [basis, setBasis] = useState("STANDALONE");
  const [drill, setDrill] = useState(null);
  const report = reports.find((r) => r.financial_year === year) || reports[0];
  const bs = useMemo(() => reconstructBalanceSheet(model, report.report_id, basis), [model, report, basis]);
  const roles = periodRoles(model, report.report_id);
  const stmt = (model.records.statement_instances || []).find((s) => s.report_instance_id === report.id && s.basis === basis);

  return (
    <div>
      <SectionHead eyebrow="Primary Financial Statements" title="Balance Sheet"
        caption="Reconstructed from governed source rows and cells. Amounts and labels are shown exactly as captured; nothing is computed or fabricated." />
      <Panel className="mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Company"><div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold"><Building2 className="h-4 w-4 text-indigo-600" />{company}</div></Field>
          <Field label="Financial Year">
            <select data-testid="bs-year" value={year} onChange={(e) => setYear(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              {years.map((y) => <option key={y}>{y}</option>)}
            </select>
          </Field>
          <Field label="Basis">
            <div className="flex rounded-lg border border-slate-200 p-0.5" data-testid="bs-basis">
              {["STANDALONE", "CONSOLIDATED"].map((b) => (
                <button key={b} data-testid={`basis-${b}`} onClick={() => setBasis(b)}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold ${basis === b ? "bg-indigo-600 text-white" : "text-slate-600"}`}>{b}</button>
              ))}
            </div>
          </Field>
          <div className="ml-auto text-right text-xs text-slate-500">
            <div>Statement: <b>{stmt ? stmt.statement : "—"}</b> · {stmt ? <StatusBadge value={stmt.status} /> : <StatusBadge value="NOT_CAPTURED" />}</div>
            <div className="mt-1">Report: <span className="font-mono">{report.report_id}</span></div>
          </div>
        </div>
      </Panel>

      <Panel>
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm text-slate-500">{bs.length} eligible source rows · {basis} basis</div>
          <div className="text-xs text-slate-500">
            Current: <b>{roles.current ? roles.current.parsed_period_identity : "NOT_CAPTURED"}</b> · Comparative: <b>{roles.comparative ? roles.comparative.parsed_period_identity : "NOT_CAPTURED"}</b>
          </div>
        </div>
        <div className="overflow-auto rounded-lg border border-slate-200">
          <table className="w-full border-collapse text-sm" data-testid="balance-sheet-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="border-b px-3 py-2 text-left font-bold text-slate-700">Line item</th>
                <th className="border-b px-3 py-2 text-left font-bold text-slate-700">Classification</th>
                <th className="border-b px-3 py-2 text-right font-bold text-slate-700">Current<div className="font-normal text-[10px] text-slate-400">{roles.current?.parsed_period_identity || ""}</div></th>
                <th className="border-b px-3 py-2 text-right font-bold text-slate-700">Comparative<div className="font-normal text-[10px] text-slate-400">{roles.comparative?.parsed_period_identity || ""}</div></th>
                <th className="border-b px-3 py-2 text-center font-bold text-slate-700">Page</th>
                <th className="border-b px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {bs.map((r) => (
                <tr key={r.row.id} data-testid="bs-row" className="border-b border-slate-100 hover:bg-indigo-50/40">
                  <td className="px-3 py-2 font-medium text-slate-800">{r.label}</td>
                  <td className="px-3 py-2"><StatusBadge value={r.classification} /></td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{r.current ?? <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{r.comparative ?? <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 text-center text-slate-500">{cellVal(r.page)}</td>
                  <td className="px-3 py-2 text-right"><button data-testid="bs-drill" onClick={() => setDrill(r)} className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-50">Source <ChevronRight className="h-3 w-3" /></button></td>
                </tr>
              ))}
              {bs.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No source rows for this basis. Try the other basis.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>

      <Drawer open={Boolean(drill)} onClose={() => setDrill(null)} title="Source drill-down">
        {drill && (
          <div className="space-y-4 text-sm">
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="font-bold text-slate-800">{drill.label}</div>
              <div className="mt-1 text-slate-500">Raw text: <span className="font-mono text-xs">{drill.row.raw_text}</span></div>
              <div className="mt-1 flex gap-4 text-xs text-slate-500"><span>Page {cellVal(drill.page)}</span><span>Basis {cellVal(drill.row.basis)}</span><span>Order {drill.row.source_order}</span></div>
              {drill.eligibility && <div className="mt-1 text-xs text-slate-500">Eligibility: <StatusBadge value={drill.classification} /> — {drill.eligibility.reason}</div>}
            </div>
            <div>
              <div className="mb-1 font-bold text-slate-700">Source cells ({drill.cells.length})</div>
              <div className="max-h-60 overflow-auto rounded border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50"><tr><th className="px-2 py-1 text-left">Text</th><th className="px-2 py-1">x0</th><th className="px-2 py-1">y0</th><th className="px-2 py-1">State</th><th className="px-2 py-1">Atoms</th></tr></thead>
                  <tbody>
                    {drill.cells.map((c) => {
                      const atoms = atomsForCell(model, c.id);
                      return <tr key={c.id} className="border-t border-slate-100"><td className="px-2 py-1 font-mono">{c.raw_text}</td><td className="px-2 py-1 text-center">{c.x0}</td><td className="px-2 py-1 text-center">{c.y0}</td><td className="px-2 py-1 text-center">{cellVal(c.source_state)}</td><td className="px-2 py-1 text-center font-bold">{atoms.length}</td></tr>;
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1"><span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</span>{children}</label>
);

/* ---------------- Profit & Loss (not available) ---------------- */
const ProfitLossPage = () => (
  <div>
    <SectionHead eyebrow="Primary Financial Statements" title="Profit & Loss" />
    <Panel className="flex flex-col items-center justify-center py-20 text-center" data-testid="pl-not-available">
      <FileBarChart2 className="mb-3 h-10 w-10 text-slate-300" />
      <div className="text-lg font-extrabold text-slate-700">DATA NOT YET AVAILABLE IN GOVERNED CONTRACT</div>
      <p className="mt-2 max-w-md text-sm text-slate-500">The governed contract currently supplies Balance Sheet evidence only. Profit & Loss records will appear here once Codex adds them to the contract. No values are fabricated.</p>
    </Panel>
  </div>
);

/* ---------------- Exceptions & human review ---------------- */
function ExceptionsPage() {
  const model = useModel();
  const [reviews, setReviews] = useState({});
  const rows = model.records.source_rows.filter(
    (r) => isSentinel(r.basis) || (model.records.structural_eligibility.find((e) => e.source_row_id === r.id)?.classification !== "FINANCIAL_ELIGIBLE")
  );
  const set = (id, status) => setReviews((s) => ({ ...s, [id]: status }));
  return (
    <div>
      <SectionHead eyebrow="Governance" title="Exceptions & Human Review"
        caption="Source rows with ambiguous basis or non-financial classification are routed for human review. Review decisions are session-local and never alter governed values." />
      <Panel>
        <div className="mb-2 text-sm text-slate-500">{rows.length} rows flagged for review</div>
        <div className="overflow-auto rounded-lg border border-slate-200" style={{ maxHeight: 560 }}>
          <table className="w-full text-sm" data-testid="exceptions-table">
            <thead className="sticky top-0 bg-slate-50"><tr>
              <th className="px-3 py-2 text-left font-bold text-slate-700">Line item</th><th className="px-3 py-2 text-left font-bold text-slate-700">Basis</th>
              <th className="px-3 py-2 text-left font-bold text-slate-700">Classification</th><th className="px-3 py-2 text-center font-bold text-slate-700">Page</th>
              <th className="px-3 py-2 text-left font-bold text-slate-700">Review</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const cls = model.records.structural_eligibility.find((e) => e.source_row_id === r.id)?.classification || "UNCLASSIFIED";
                return (
                  <tr key={r.id} data-testid="exception-row" className="border-b border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-800">{r.raw_text}</td>
                    <td className="px-3 py-2">{cellVal(r.basis)}</td>
                    <td className="px-3 py-2"><StatusBadge value={cls} /></td>
                    <td className="px-3 py-2 text-center text-slate-500">{cellVal(r.page)}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        {["ACCEPT", "REJECT", "NEEDS_INFO"].map((a) => (
                          <button key={a} data-testid={`review-${a}-${r.id}`} onClick={() => set(r.id, a)}
                            className={`rounded px-2 py-1 text-xs font-semibold ${reviews[r.id] === a ? "bg-indigo-600 text-white" : "border border-slate-200 text-slate-600"}`}>{a.replace("_", " ")}</button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No exceptions — all rows are financially eligible.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

/* ---------------- Certification ---------------- */
function CertificationPage() {
  const model = useModel();
  const run = model.records.certification_runs[0];
  const gate = model.records.certification_gate_summary[0];
  const e = model.evidence;
  const Metric = ({ label, value }) => (
    <Panel className="flex-1"><div className="text-[11px] font-bold uppercase text-slate-400">{label}</div><div className="mt-1 text-2xl font-extrabold text-slate-900">{value}</div></Panel>
  );
  return (
    <div className="space-y-6">
      <SectionHead eyebrow="Governance" title="Certification Status & Evidence" caption="Statuses are shown exactly as produced by the pipeline. FAIL and NOT_RUN are preserved (fail-closed)." />
      <div className="flex flex-wrap gap-3">
        <Metric label="Run status" value={run ? <StatusBadge value={run.run_status || run.status} /> : "—"} />
        <Metric label="Gate summary" value={gate ? <StatusBadge value={gate.status} /> : "—"} />
        <Metric label="Referential integrity" value={<StatusBadge value={e.referentialIntegrity} />} />
        <Metric label="Orphans" value={e.orphanRecordCount} />
      </div>
      {gate && (
        <Panel>
          <div className="mb-2 font-bold text-slate-800">Certification gates</div>
          <div className="grid gap-2 md:grid-cols-2">
            {Object.entries(gate).filter(([k]) => !["id", "status", "reason"].includes(k)).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded border border-slate-100 px-3 py-1.5 text-sm"><span className="text-slate-600">{humanize(k)}</span><StatusBadge value={v} /></div>
            ))}
          </div>
          {gate.reason && <div className="mt-2 text-xs text-slate-500">Reason: {gate.reason}</div>}
        </Panel>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel><div className="mb-2 font-bold text-slate-800">Pipeline stages</div><DataTable testid="stages-table" records={model.records.pipeline_stage_runs} pageSize={8} /></Panel>
        <Panel><div className="mb-2 font-bold text-slate-800">Semantic & Determinism</div><DataTable testid="semantic-table" records={[...model.records.semantic_comparison_results, ...model.records.determinism_artifacts]} pageSize={8} /></Panel>
        <Panel><div className="mb-2 font-bold text-slate-800">Tests & Builds</div><DataTable testid="tests-table" records={[...model.records.test_execution_results, ...model.records.build_execution_results]} pageSize={8} /></Panel>
        <Panel><div className="mb-2 font-bold text-slate-800">Evidence artifacts</div><DataTable testid="evidence-table" records={model.records.evidence_artifacts} pageSize={8} /></Panel>
      </div>
      <Panel>
        <div className="mb-2 flex items-center gap-2 font-bold text-slate-800"><Lock className="h-4 w-4 text-amber-600" />Heldout firewall</div>
        <div className="mb-2 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">ICICI and SBI corpora remain sealed. This module only audits access.</div>
        <DataTable testid="heldout-table" records={model.records.heldout_access_audit} pageSize={8} />
      </Panel>
    </div>
  );
}

/* ---------------- Admin Data Architecture ---------------- */
function ArchitecturePage() {
  const model = useModel();
  const [tab, setTab] = useState("catalogue");
  const tabs = [
    { id: "catalogue", label: "Table Catalogue", icon: Table2 },
    { id: "dictionary", label: "Field Dictionary", icon: BookOpen },
    { id: "diagram", label: "Relationship Diagram", icon: Network },
    { id: "matrix", label: "Relationship Matrix", icon: Grid3x3 },
    { id: "lineage", label: "Data Lineage", icon: GitBranch },
  ];
  const [dictTable, setDictTable] = useState("certification_runs");
  const [relDialog, setRelDialog] = useState(null);
  return (
    <div>
      <SectionHead eyebrow="Admin" title="Data Architecture" caption="Contract-driven catalogue generated from governed schemas, data dictionary and relationship metadata." />
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`arch-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${tab === t.id ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <t.icon className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === "catalogue" && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="table-catalogue">
          {model.tables.map((t) => (
            <Panel key={t.table}>
              <div className="flex items-start justify-between">
                <div><div className="font-extrabold text-slate-900">{t.displayName}</div><div className="font-mono text-xs text-indigo-600">{t.table}</div></div>
                <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">{t.canonical === false ? "DERIVED" : "CANONICAL"}</span>
              </div>
              <p className="my-2 min-h-[48px] text-sm text-slate-500">{t.description}</p>
              <dl className="space-y-1 text-xs">
                <Row k="Grain" v={t.grain} /><Row k="Rows" v={String(t.rowCount)} /><Row k="Primary key" v={t.primaryKey} />
                <Row k="Foreign keys" v={t.foreignKeys.length ? t.foreignKeys.map((f) => f.field).join(", ") : "—"} />
                <Row k="Parents" v={t.parentTables.join(", ") || "—"} /><Row k="Children" v={t.childTables.join(", ") || "—"} />
                <Row k="Fields" v={String(t.fields.length)} />
              </dl>
              <button data-testid={`catalogue-open-${t.table}`} onClick={() => { setDictTable(t.table); setTab("dictionary"); }} className="mt-3 w-full rounded-lg border border-slate-200 py-1.5 text-sm font-semibold text-indigo-600 hover:bg-indigo-50">Open Field Dictionary →</button>
            </Panel>
          ))}
        </div>
      )}

      {tab === "dictionary" && <FieldDictionary model={model} dictTable={dictTable} setDictTable={setDictTable} />}
      {tab === "diagram" && <RelationshipDiagram model={model} onOpenTable={(t) => { setDictTable(t); setTab("dictionary"); }} onEdge={setRelDialog} />}
      {tab === "matrix" && <RelationshipMatrix model={model} onRel={setRelDialog} />}
      {tab === "lineage" && <DataLineage model={model} />}

      <Drawer open={Boolean(relDialog)} onClose={() => setRelDialog(null)} title="Relationship definition">
        {relDialog && (
          <div className="space-y-2 text-sm" data-testid="relationship-dialog">
            {[["Parent", `${relDialog.parentTable}.${relDialog.parentField}`], ["Child", `${relDialog.childTable}.${relDialog.childField}`],
              ["Cardinality", relDialog.cardinality], ["Mandatory", relDialog.mandatory], ["Join", relDialog.joinDirection],
              ["Description", relDialog.description], ["Parent records", relDialog.parentCount], ["Child records", relDialog.childCount],
              ["Matched", relDialog.matchedCount], ["Orphans", relDialog.orphanCount]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3"><span className="text-slate-500">{k}</span><span className="text-right font-medium text-slate-800">{String(v)}</span></div>
            ))}
            <div className="flex justify-between"><span className="text-slate-500">Referential integrity</span><StatusBadge value={relDialog.integrity} /></div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
const Row = ({ k, v }) => (<div className="flex justify-between gap-2"><dt className="text-slate-400">{k}</dt><dd className="text-right font-medium text-slate-700">{v}</dd></div>);

function FieldDictionary({ model, dictTable, setDictTable }) {
  const t = model.tables.find((x) => x.table === dictTable) || model.tables[0];
  const sample = (model.records[t.table] || [])[0] || {};
  return (
    <div>
      <div className="mb-3 flex items-center gap-3">
        <select data-testid="dictionary-select" value={t.table} onChange={(e) => setDictTable(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          {model.tables.map((x) => <option key={x.table} value={x.table}>{x.displayName} ({x.table})</option>)}
        </select>
        <span className="text-sm text-slate-500">{t.fields.length} fields · {t.rowCount} rows</span>
      </div>
      <Panel className="mb-3"><div className="font-extrabold text-slate-900">{t.displayName} <span className="font-mono text-xs text-indigo-600">{t.table}</span></div><p className="mt-1 text-sm text-slate-500">{t.description}</p></Panel>
      <div className="overflow-auto rounded-lg border border-slate-200" style={{ maxHeight: 620 }}>
        <table className="w-full text-sm" data-testid="field-dictionary-table">
          <thead className="sticky top-0 bg-slate-50"><tr>
            {["Field / Label", "Type", "Nullable", "Key", "References", "Description", "Example (HDFC)", "Provenance"].map((h) => <th key={h} className="px-3 py-2 text-left font-bold text-slate-700">{h}</th>)}
          </tr></thead>
          <tbody>
            {t.fields.map((f) => (
              <tr key={f.name} data-testid={`field-${f.name}`} className="border-b border-slate-100">
                <td className="px-3 py-2"><div className="font-semibold text-slate-800">{humanize(f.name)}</div><div className="font-mono text-[11px] text-indigo-600">{f.name}</div></td>
                <td className="px-3 py-2"><code className="text-xs">{f.type}</code></td>
                <td className="px-3 py-2">{f.required ? <span className="rounded border border-amber-300 px-1.5 text-xs text-amber-700">required</span> : <span className="rounded border border-slate-200 px-1.5 text-xs text-slate-500">nullable</span>}</td>
                <td className="px-3 py-2">{f.isPrimaryKey && <KeyBadge kind="PK" />}{f.isForeignKey && <KeyBadge kind="FK" />}{!f.isPrimaryKey && !f.isForeignKey && "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{f.isForeignKey ? `${f.referencedTable}.${f.referencedField}` : "—"}</td>
                <td className="max-w-xs px-3 py-2 text-xs text-slate-500">{f.description}</td>
                <td className="max-w-[180px] truncate px-3 py-2 text-xs">{cellVal(sample[f.name])}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{f.provenance}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RelationshipDiagram({ model, onOpenTable, onEdge }) {
  const [focus, setFocus] = useState("");
  const [nb, setNb] = useState(false);
  const depth = useMemo(() => {
    const d = {}; model.tables.forEach((t) => (d[t.table] = 0));
    for (let i = 0; i < model.tables.length; i++) model.relationships.forEach((r) => { if (d[r.childTable] < d[r.parentTable] + 1) d[r.childTable] = d[r.parentTable] + 1; });
    return d;
  }, [model]);
  const visible = useMemo(() => {
    if (nb && focus) { const keep = new Set([focus]); model.relationships.forEach((r) => { if (r.parentTable === focus) keep.add(r.childTable); if (r.childTable === focus) keep.add(r.parentTable); }); return model.tables.filter((t) => keep.has(t.table)); }
    return model.tables;
  }, [model, nb, focus]);
  const rels = model.relationships.filter((r) => visible.some((t) => t.table === r.parentTable) && visible.some((t) => t.table === r.childTable));
  const COL = 250, ROW = 92, NW = 190, NH = 62;
  const pos = {}; const tiers = {};
  visible.forEach((t) => { const k = depth[t.table] || 0; (tiers[k] = tiers[k] || []).push(t.table); });
  Object.entries(tiers).forEach(([k, list]) => list.forEach((tb, i) => (pos[tb] = { x: Number(k) * COL + 20, y: i * ROW + 20 })));
  const maxTier = Math.max(0, ...visible.map((t) => depth[t.table] || 0));
  const maxRows = Math.max(1, ...Object.values(tiers).map((l) => l.length));
  const W = (maxTier + 1) * COL + 60, H = maxRows * ROW + 40;
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select data-testid="diagram-focus" value={focus} onChange={(e) => { setFocus(e.target.value); if (e.target.value) setNb(true); }} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <option value="">Focus on a table…</option>
          {model.tables.map((t) => <option key={t.table} value={t.table}>{t.displayName}</option>)}
        </select>
        <button data-testid="diagram-toggle" disabled={!focus} onClick={() => setNb((n) => !n)} className={`rounded-lg px-3 py-2 text-sm font-semibold ${nb ? "bg-indigo-600 text-white" : "border border-slate-200 text-slate-600"} disabled:opacity-40`}>{nb ? "Selected neighbourhood" : "Complete model"}</button>
        {focus && <button onClick={() => { setFocus(""); setNb(false); }} className="text-sm text-slate-500">Clear</button>}
        <span className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-600">{visible.length} tables · {rels.length} relationships</span>
      </div>
      <div className="overflow-auto rounded-xl border border-slate-200 bg-slate-50 p-2" data-testid="relationship-diagram">
        <div className="relative" style={{ width: W, height: H, minWidth: "100%" }}>
          <svg width={W} height={H} className="absolute inset-0">
            {rels.map((r) => {
              const p = pos[r.parentTable], c = pos[r.childTable]; if (!p || !c) return null;
              const x1 = p.x + NW, y1 = p.y + NH / 2, x2 = c.x, y2 = c.y + NH / 2, mx = (x1 + x2) / 2;
              return (
                <g key={`${r.parentTable}-${r.childTable}-${r.childField}`} className="cursor-pointer" onClick={() => onEdge(r)}>
                  <path d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`} fill="none" stroke={r.integrity === "PASS" ? "#93b4dc" : "#e11d48"} strokeWidth="1.6" />
                  <rect x={mx - 15} y={(y1 + y2) / 2 - 9} width="30" height="18" rx="4" fill="#fff" stroke="#e2e8f0" />
                  <text x={mx} y={(y1 + y2) / 2 + 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#4f46e5">{r.cardinality}</text>
                </g>
              );
            })}
          </svg>
          {visible.map((t) => {
            const p = pos[t.table]; if (!p) return null;
            return (
              <div key={t.table} data-testid={`diagram-node-${t.table}`} onClick={() => onOpenTable(t.table)}
                className={`absolute cursor-pointer rounded-lg border-2 bg-white p-2 shadow-sm hover:border-indigo-500 ${focus === t.table ? "border-indigo-600" : "border-slate-200"}`}
                style={{ left: p.x, top: p.y, width: NW, height: NH }}>
                <div className="truncate text-xs font-extrabold text-slate-800">{t.displayName}</div>
                <div className="font-mono text-[10px] text-slate-400">{t.table}</div>
                <div className="mt-1 flex items-center justify-between"><span className="rounded bg-slate-100 px-1.5 text-[10px]">{t.rowCount} rows</span><span className={`text-[10px] font-bold ${t.childTables.length || t.parentTables.length ? "text-emerald-600" : "text-slate-400"}`}>{t.childTables.length || t.parentTables.length ? "RI PASS" : "standalone"}</span></div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="mt-2 text-xs text-slate-500">Click a table → Field Dictionary · click an edge → relationship definition · focus a table for its immediate parents/children.</p>
    </div>
  );
}

function RelationshipMatrix({ model, onRel }) {
  const [q, setQ] = useState("");
  const rels = model.relationships.filter((r) => `${r.parentTable} ${r.childTable} ${r.cardinality} ${r.description}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="mb-2"><input data-testid="matrix-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search relationships…" className="w-72 rounded-lg border border-slate-200 px-3 py-1.5 text-sm" /></div>
      <div className="overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm" data-testid="relationship-matrix-table">
          <thead className="bg-slate-50"><tr>{["Parent table", "Parent field", "Child table", "Child field", "Cardinality", "Description", "Matched", "Orphans", "Integrity"].map((h) => <th key={h} className="px-3 py-2 text-left font-bold text-slate-700">{h}</th>)}</tr></thead>
          <tbody>
            {rels.map((r) => (
              <tr key={`${r.parentTable}.${r.parentField}-${r.childTable}.${r.childField}`} data-testid="matrix-row" onClick={() => onRel(r)} className="cursor-pointer border-b border-slate-100 hover:bg-indigo-50/40">
                <td className="px-3 py-2 font-mono text-xs">{r.parentTable}</td><td className="px-3 py-2 font-mono text-xs">{r.parentField}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.childTable}</td><td className="px-3 py-2 font-mono text-xs">{r.childField}</td>
                <td className="px-3 py-2"><span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-bold">{r.cardinality}</span> <span className="text-[11px] text-slate-400">{r.mandatory}</span></td>
                <td className="max-w-xs px-3 py-2 text-xs text-slate-500">{r.description}</td>
                <td className="px-3 py-2">{r.matchedCount}</td>
                <td className={`px-3 py-2 font-bold ${r.orphanCount ? "text-rose-600" : "text-emerald-600"}`}>{r.orphanCount}</td>
                <td className="px-3 py-2"><StatusBadge value={r.integrity} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DataLineage({ model }) {
  const chain = [["report_instances", "Reports"], ["statement_instances", "Statements"], ["source_rows", "Rows"], ["source_cells", "Cells"], ["source_atoms", "Atoms"]];
  return (
    <div className="space-y-4">
      <Panel>
        <div className="flex flex-wrap items-center gap-2" data-testid="lineage-flow">
          {chain.map(([tbl, label], i) => (
            <React.Fragment key={tbl}>
              <div className="flex-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
                <div className="font-extrabold text-slate-800">{label}</div><div className="font-mono text-[10px] text-indigo-600">{tbl}</div>
                <div className="text-2xl font-extrabold text-slate-900">{(model.records[tbl] || []).length}</div>
              </div>
              {i < chain.length - 1 && <span className="text-xl font-extrabold text-indigo-500">→</span>}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-2 text-xs text-slate-500">Lineage edges: <b>{model.records.lineage_edges.length}</b> cell→atom links · header-period evidence <b>{model.records.header_period_evidence.length}</b> · structural eligibility <b>{model.records.structural_eligibility.length}</b></div>
      </Panel>
      <Panel><div className="mb-2 font-bold text-slate-800">Lineage edges (from_id cell → to_id atom)</div><DataTable testid="data-lineage-table" records={model.records.lineage_edges} /></Panel>
    </div>
  );
}

/* ---------------- Shell ---------------- */
const NAV = [
  { to: "/balance-sheet", label: "Balance Sheet", icon: FileBarChart2 },
  { to: "/profit-loss", label: "Profit & Loss", icon: FileBarChart2 },
  { to: "/exceptions", label: "Exceptions & Review", icon: AlertTriangle },
  { to: "/certification", label: "Certification", icon: ShieldCheck },
  { to: "/admin/architecture", label: "Data Architecture", icon: Database, admin: true },
];

function Shell({ children }) {
  const model = useModel();
  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900">
      <aside className="sticky top-0 flex h-screen w-64 flex-col bg-[#0f1c2e] p-4 text-slate-100">
        <div className="mb-6">
          <div className="text-[11px] font-bold uppercase tracking-widest text-sky-300">Annual Report Intelligence</div>
          <div className="text-lg font-extrabold">Primary Balance Sheet</div>
          <div className="text-xs text-slate-400">Certification & Data Explorer</div>
        </div>
        <nav className="flex-1 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.replace(/\//g, "-")}`}
              className={({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${isActive ? "bg-indigo-600 text-white" : "text-slate-300 hover:bg-white/10"}`}>
              <n.icon className="h-4 w-4" />{n.label}{n.admin && <span className="ml-auto rounded bg-white/10 px-1.5 text-[9px]">ADMIN</span>}
            </NavLink>
          ))}
        </nav>
        <NavLink to="/knowledge-studio/annual-report-intelligence" data-testid="nav-knowledge-studio"
          className="mt-3 flex items-center gap-2 rounded-lg bg-indigo-500/20 px-3 py-2 text-sm font-semibold text-sky-200 ring-1 ring-inset ring-indigo-400/30 hover:bg-indigo-500/30">
          <BookOpen className="h-4 w-4" />Knowledge Studio<span className="ml-auto rounded bg-sky-400/20 px-1.5 text-[9px]">NEW</span>
        </NavLink>

        <div className="mt-4 rounded-lg bg-white/5 p-3 text-xs">
          <div className="text-slate-400">Active source</div>
          <div data-testid="active-source" className={`font-extrabold ${model.activeSource === "SQL_API" ? "text-emerald-400" : "text-amber-300"}`}>{model.activeSource}</div>
        </div>
      </aside>
      <div className="flex-1">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-8 py-3">
          <div className="flex items-center gap-2 text-sm"><Building2 className="h-4 w-4 text-indigo-600" /><b>{model.companies.join(", ")}</b><span className="text-slate-400">· {model.contractVersion}</span></div>
          <div className="flex gap-5 text-center text-sm">
            {[["Reports", "report_instances"], ["Statements", "statement_instances"], ["Rows", "source_rows"], ["Cells", "source_cells"], ["Atoms", "source_atoms"]].map(([l, k]) => (
              <div key={k} data-testid={`count-${l.toLowerCase()}`}><div className="text-lg font-extrabold">{model.counts[k]}</div><div className="text-[10px] font-bold uppercase text-slate-400">{l}</div></div>
            ))}
          </div>
        </header>
        <main className="p-8">{children}</main>
      </div>
    </div>
  );
}

export default function LegacyApp() {
  const [model, setModel] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { loadContract().then(setModel).catch((e) => setError(String(e?.message || e))); }, []);
  if (error) return <div className="p-10 text-rose-600">Failed to load governed contract: {error}</div>;
  if (!model) return <div className="flex min-h-screen items-center justify-center text-slate-500">Loading governed contract…</div>;
  return (
    <ContractCtx.Provider value={model}>
      <Shell>
        <Routes>
          <Route path="/" element={<Navigate to="/balance-sheet" replace />} />
          <Route path="/balance-sheet" element={<BalanceSheetPage />} />
          <Route path="/profit-loss" element={<ProfitLossPage />} />
          <Route path="/exceptions" element={<ExceptionsPage />} />
          <Route path="/certification" element={<CertificationPage />} />
          <Route path="/admin/architecture" element={<ArchitecturePage />} />
          <Route path="*" element={<Navigate to="/balance-sheet" replace />} />
        </Routes>
      </Shell>
    </ContractCtx.Provider>
  );
}
